import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import api from '../utils/api'
import { storage } from '../utils/storage'

interface User {
  id: string
  username: string
  name: string
  email?: string
  role: string
  facilityAccess: boolean
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = storage.getItem('accessToken')
      if (token) {
        const response = await api.get('/auth/verify')
        setUser(response.data.user)
      }
    } catch (_error) {
      storage.removeItem('accessToken')
      storage.removeItem('refreshToken')
    } finally {
      setLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post('/auth/login', {
        login: username,
        password,
      })

      const { user, accessToken, refreshToken } = response.data

      storage.setItem('accessToken', accessToken)
      storage.setItem('refreshToken', refreshToken)
      setUser(user)
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '登入失敗')
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (_error) {
      // 忽略登出錯誤
    } finally {
      storage.removeItem('accessToken')
      storage.removeItem('refreshToken')
      setUser(null)
    }
  }

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}


